/**
 * Edit Command Autocomplete Component
 * Shows list of editable open tabs AND recent editable documents for @edit command
 * Supports filtering by query text
 */

import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import type { OpenedDocument } from '../contexts/AIContext';
import { logger } from '../utils/logger';
import { useAutocompleteKeyboard } from '../hooks/useAutocompleteKeyboard';

interface RecentDocument {
  id: string;
  documentNumber?: string;
  projectId?: string;
  title: string;
  type: 'project' | 'company' | 'standard' | 'prior-project';
  canEdit?: boolean;
  status?: string;  // Document status: Draft, In Review, Approved, Released
  currentRevision?: string;  // Current revision: Draft, A, B, etc.
}

interface EditCommandAutocompleteProps {
  documents: OpenedDocument[];
  query: string;
  onSelect: (tabId: number) => void;
  onOpenAndEdit: (doc: RecentDocument) => void;
  onClose: () => void;
}

type SelectableItem = 
  | { kind: 'open'; doc: OpenedDocument }
  | { kind: 'recent'; doc: RecentDocument };

export default function EditCommandAutocomplete({
  documents,
  query,
  onSelect,
  onOpenAndEdit,
  onClose
}: EditCommandAutocompleteProps) {
  const [recentDocs, setRecentDocs] = useState<RecentDocument[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter to only editable documents (project docs, company docs - not references or pages)
  const editableDocs = useMemo(() => documents.filter(doc => 
    doc.canEdit === true && 
    doc.tabType !== 'page' && 
    doc.tabType !== 'plan'
  ), [documents]);

  // Load recent documents from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('aeroRecentDocuments');
      if (saved) {
        const recent: RecentDocument[] = JSON.parse(saved);
        // Filter to only editable docs that aren't already open
        // Check both id AND documentNumber for deduplication (same doc can have different IDs)
        const openIds = new Set(documents.map(d => d.id));
        const openDocNumbers = new Set(documents.map(d => d.documentNumber).filter(Boolean));
        
        // Deduplicate by documentNumber (same doc can appear with different titles/ids)
        const seenDocNumbers = new Set<string>();
        const editableRecent = recent.filter(doc => {
          // Must be editable
          if (doc.canEdit !== true) return false;
          // Must be project or company doc
          if (doc.type !== 'project' && doc.type !== 'company') return false;
          // Skip if already open (by id or documentNumber)
          if (openIds.has(doc.id)) return false;
          if (doc.documentNumber && openDocNumbers.has(doc.documentNumber)) return false;
          // Deduplicate by documentNumber
          const docNum = doc.documentNumber || doc.id;
          if (seenDocNumbers.has(docNum)) return false;
          seenDocNumbers.add(docNum);
          return true;
        });
        setRecentDocs(editableRecent.slice(0, 10)); // Keep more for filtering
      }
    } catch (error) {
      logger.error('Failed to load recent documents:', error);
    }
  }, [documents]);

  // Filter documents by query
  const filteredEditableDocs = useMemo(() => {
    if (!query.trim()) return editableDocs;
    const lowerQuery = query.toLowerCase();
    return editableDocs.filter(doc => 
      doc.title.toLowerCase().includes(lowerQuery) ||
      doc.documentNumber?.toLowerCase().includes(lowerQuery) ||
      doc.type?.toLowerCase().includes(lowerQuery)
    );
  }, [editableDocs, query]);

  const filteredRecentDocs = useMemo(() => {
    if (!query.trim()) return recentDocs.slice(0, 5);
    const lowerQuery = query.toLowerCase();
    return recentDocs.filter(doc => 
      doc.title.toLowerCase().includes(lowerQuery) ||
      doc.documentNumber?.toLowerCase().includes(lowerQuery) ||
      doc.type?.toLowerCase().includes(lowerQuery)
    ).slice(0, 5);
  }, [recentDocs, query]);

  // Build combined list for navigation
  const allItems: SelectableItem[] = useMemo(() => [
    ...filteredEditableDocs.map(doc => ({ kind: 'open' as const, doc })),
    ...filteredRecentDocs.map(doc => ({ kind: 'recent' as const, doc })),
  ], [filteredEditableDocs, filteredRecentDocs]);

  const handleSelect = useCallback((index: number) => {
    const selected = allItems[index];
    if (selected) {
      if (selected.kind === 'open') {
        onSelect(selected.doc.tabId);
      } else {
        onOpenAndEdit(selected.doc);
      }
    }
  }, [allItems, onSelect, onOpenAndEdit]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: allItems.length,
    onSelect: handleSelect,
    onClose,
    resetDeps: [query],
  });

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getTypeBadge = (type: string | undefined) => {
    switch (type) {
      case 'project':
        return { icon: '📄', label: 'Project Doc', className: 'bg-info-light text-info' };
      case 'company':
        return { icon: '📋', label: 'Company Doc', className: 'bg-secondary text-secondary-foreground' };
      default:
        return { icon: '📄', label: 'Document', className: 'bg-muted text-muted-foreground' };
    }
  };

  const getStatusBadge = (status: string | undefined, revision: string | undefined) => {
    const rev = revision || 'Draft';
    switch (status) {
      case 'Draft':
        return { label: `Draft (Rev ${rev})`, className: 'bg-muted text-muted-foreground', editable: true };
      case 'In Review':
        return { label: `In Review (Rev ${rev})`, className: 'bg-warning-light text-warning', editable: true };
      case 'Approved':
        return { label: `Approved (Rev ${rev})`, className: 'bg-info-light text-info', editable: false };
      case 'Released':
        return { label: `Released (Rev ${rev})`, className: 'bg-success-light text-success', editable: false };
      default:
        return { label: rev ? `Rev ${rev}` : 'Unknown', className: 'bg-muted text-muted-foreground', editable: true };
    }
  };

  // Highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);
    if (index === -1) return text;
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-highlight rounded px-0.5">{text.slice(index, index + query.length)}</span>
        {text.slice(index + query.length)}
      </>
    );
  };

  const hasNoResults = allItems.length === 0;
  const isFiltering = query.trim().length > 0;

  if (hasNoResults && !isFiltering) {
    return (
      <div
        ref={containerRef}
        className="command-autocomplete"
      >
        <div className="px-4 py-2 bg-warning-light border-b border-warning">
          <div className="text-xs font-semibold text-warning">
            ✏️ Edit Document
          </div>
        </div>
        <div className="px-4 py-8 text-center">
          <div className="text-2xl mb-2">📭</div>
          <div className="text-sm text-muted-foreground">
            No editable documents available
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            Use @open to open a project or company document first
          </div>
        </div>
        <div className="px-4 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
          <span>Esc Close</span>
        </div>
      </div>
    );
  }

  // Track current index for rendering
  let currentIndex = 0;

  return (
    <div
      ref={containerRef}
      className="command-autocomplete"
    >
      <div className="px-4 py-2 bg-warning-light border-b border-warning">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold text-warning">
            ✏️ Edit Document
          </div>
          {isFiltering && (
            <div className="text-xs text-warning">
              Filtering: "{query}"
            </div>
          )}
        </div>
      </div>

      {hasNoResults && isFiltering ? (
        <div className="px-4 py-8 text-center">
          <div className="text-2xl mb-2">🔍</div>
          <div className="text-sm text-muted-foreground">
            No documents match "{query}"
          </div>
        </div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          {/* Currently Open Editable Documents */}
          {filteredEditableDocs.length > 0 && (
            <>
              <div className="px-4 py-2 bg-muted text-xs font-semibold text-muted-foreground uppercase">
                Currently Open {isFiltering && `(${filteredEditableDocs.length})`}
              </div>
              {filteredEditableDocs.map((doc) => {
                const badge = getTypeBadge(doc.type);
                const isSelected = currentIndex === selectedIndex;
                const itemIndex = currentIndex;
                currentIndex++;

                return (
                  <div
                    key={`open-${doc.tabId}`}
                    data-index={itemIndex}
                    onClick={() => onSelect(doc.tabId)}
                    className={`px-4 py-3 cursor-pointer border-b border-border transition-colors ${
                      isSelected
                        ? 'bg-warning-light'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">{badge.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            Tab {doc.tabId}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${badge.className}`}>
                            {badge.label}
                          </span>
                        </div>
                        <div className="font-medium text-foreground truncate">
                          {highlightMatch(doc.title, query)}
                        </div>
                        {doc.documentNumber && (
                          <div className="text-xs text-muted-foreground font-mono mt-1">
                            {highlightMatch(doc.documentNumber, query)}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-warning">
                        ✏️
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Recent Editable Documents (not currently open) */}
          {filteredRecentDocs.length > 0 && (
            <>
              <div className="px-4 py-2 bg-muted text-xs font-semibold text-muted-foreground uppercase">
                Recent Documents {isFiltering && `(${filteredRecentDocs.length})`}
              </div>
              {filteredRecentDocs.map((doc) => {
                const badge = getTypeBadge(doc.type);
                const statusBadge = getStatusBadge(doc.status, doc.currentRevision);
                const isSelected = currentIndex === selectedIndex;
                const itemIndex = currentIndex;
                currentIndex++;

                return (
                  <div
                    key={`recent-${doc.id}`}
                    data-index={itemIndex}
                    onClick={() => onOpenAndEdit(doc)}
                    className={`px-4 py-3 cursor-pointer border-b border-border transition-colors ${
                      isSelected
                        ? 'bg-warning-light'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">{badge.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs text-warning bg-warning-light px-1.5 py-0.5 rounded">
                            {statusBadge.editable ? 'Open & Edit' : 'Open (Read-only)'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${badge.className}`}>
                            {badge.label}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${statusBadge.className}`}>
                            {statusBadge.label}
                          </span>
                        </div>
                        <div className="font-medium text-foreground truncate">
                          {highlightMatch(doc.title, query)}
                        </div>
                        {doc.documentNumber && (
                          <div className="text-xs text-muted-foreground font-mono mt-1">
                            {highlightMatch(doc.documentNumber, query)}
                          </div>
                        )}
                        {!statusBadge.editable && (
                          <div className="text-xs text-warning mt-1">
                            ⚠️ Create new revision to edit
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-warning">
                        {statusBadge.editable ? '📂✏️' : '📂🔒'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      <div className="px-4 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <span>↑↓ Navigate • Enter Edit • Esc Cancel</span>
          <span>
            {filteredEditableDocs.length > 0 && `${filteredEditableDocs.length} open`}
            {filteredEditableDocs.length > 0 && filteredRecentDocs.length > 0 && ' • '}
            {filteredRecentDocs.length > 0 && `${filteredRecentDocs.length} recent`}
          </span>
        </div>
      </div>
    </div>
  );
}
